"use client";

import { useEffect, useState } from "react";
import { shippingService } from "@/lib/api/services";
import type { ShippingAddress, ShippingCity } from "@/types";

type Translate = (key: string) => string;

const EMPTY_ADDRESS: ShippingAddress = {
  customerName: "",
  mobile: "",
  email: "",
  cityId: 0,
  address: "",
  details: "",
};

export function useShippingAddressForm(
  t: Translate,
  options?: {
    initialData?: Partial<ShippingAddress>;
    enabled?: boolean;
  },
) {
  const enabled = options?.enabled ?? true;

  const [isLoadingCities, setIsLoadingCities] = useState(enabled);
  const [cities, setCities] = useState<ShippingCity[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ShippingAddress>({
    ...EMPTY_ADDRESS,
    ...(options?.initialData ?? {}),
    cityId: Number(options?.initialData?.cityId || 0),
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!enabled) {
      setIsLoadingCities(false);
      return;
    }

    let cancelled = false;
    async function fetchCities() {
      setIsLoadingCities(true);
      try {
        const response = await shippingService.getCities();
        if (!cancelled && response.success && response.data) {
          const raw = response.data.cities || [];
          const mapped: ShippingCity[] = raw.map((c: any) => ({
            id: c.id ?? c.Id,
            name: c.name || c.NameEn || c.Name || "",
            nameAr: c.nameAr || c.Name || c.name || "",
            shippingCost: c.shippingCost,
          }));
          setCities(mapped);
        }
      } catch {
        if (!cancelled) {
          setError(t("checkout.errors.failedToLoadCities"));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCities(false);
        }
      }
    }
    void fetchCities();
    return () => {
      cancelled = true;
    };
  }, [enabled, t]);

  const handleInputChange = (field: keyof ShippingAddress, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.customerName.trim()) {
      errors.customerName = t("checkout.shipping.errors.nameRequired");
    }

    if (!formData.mobile.trim()) {
      errors.mobile = t("checkout.shipping.errors.mobileRequired");
    } else if (!/^05\d{8}$/.test(formData.mobile.replace(/\s/g, ""))) {
      errors.mobile = t("checkout.shipping.errors.mobileInvalid");
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("checkout.shipping.errors.emailInvalid");
    }

    if (!formData.cityId) {
      errors.cityId = t("checkout.shipping.errors.cityRequired");
    }

    if (!formData.address.trim()) {
      errors.address = t("checkout.shipping.errors.addressRequired");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return {
    isLoadingCities,
    cities,
    error,
    setError,
    formData,
    formErrors,
    handleInputChange,
    validateForm,
    setFormData,
  };
}
